import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="5739" className={cn("flex gap-4", isMobile ? "flex-col items-start" : "items-center")}>
        {[1, 2, 3].map(i => <div key={i} className="h-6 w-20 bg-secondary/20 animate-pulse rounded-none" data-index={i} />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col space-y-6 p-6">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="font-mono text-lg uppercase tracking-widest text-foreground/90 hover:text-primary transition-colors" onClick={closeMobileMenu} data-index={i}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative font-mono text-[12px] font-medium uppercase tracking-widest text-foreground transition-colors hover:text-foreground">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="system-header" className={cn("dark sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-border/10 shadow-lg" : "bg-background")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-6 md:px-12">
        {}
        <div className="flex-shrink-0">
          <Link to="/ControlCenterPage" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598652/img/pixelforge_logo.png" alt="Exergy Systems" className="h-7 w-auto object-contain brightness-0 invert opacity-90" />
          </Link>
        </div>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="16" className="flex items-center">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-primary/10 hover:text-primary rounded-none border border-transparent hover:border-primary/20" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full border-b border-border/10 bg-background/95 backdrop-blur-xl md:hidden animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="16">
            <HeaderMenuConsumer isMobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}